import React, { useState, useEffect } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  Variants,
  MotionValue,
  useTransform as useMorphTransform,
} from "motion/react";
import clsx from "clsx";
import { useCursor } from "../../../context/cursorUtils";
import { GoChevronUp } from "react-icons/go";

const MorphingCursor = ({
  shapePaths,
  shapeColors,
  smoothCursorX,
  smoothCursorY,
}: {
  shapePaths: string[];
  shapeColors: string[];
  smoothCursorX: MotionValue<number>;
  smoothCursorY: MotionValue<number>;
}) => {
  const [boxState, setBoxState] = useState("closed"); // Start with box closed and shapes inside
  const [showBox, setShowBox] = useState(true); // Show box immediately
  // Use only the first shape and color, no morphing between shapes
  const currentPath = shapePaths[0] || "";
  const currentColor = shapeColors[0] || "#DB0703";

  const isHeartShape = currentColor === "#ff8cb1";
  const isCircles = currentColor === "#da00ec";
  // const isBox = currentColor === "#00ffff";
  const isBulb = currentColor === "#ffd500";

  // Use useMorphTransform (aliased from useTransform) to offset the cursor by 100
  const offsetCursorXBulb = useMorphTransform(smoothCursorX, (v) => v - 200);
  const offsetCursorYBulb = useMorphTransform(smoothCursorY, (v) => v - 150);

  const offsetCursorX = useMorphTransform(smoothCursorX, (v) => v - 100);
  const offsetCursorY = useMorphTransform(smoothCursorY, (v) => v - 60);

  // Specific offset for circles - center the 100x100 SVG
  const offsetCursorXCircles = useMorphTransform(smoothCursorX, (v) => v - 100);
  const offsetCursorYCircles = useMorphTransform(smoothCursorY, (v) => v - 150);

  // Animation cycle controller - only run when isCircles is true
  useEffect(() => {
    console.log("useEffect triggered, isCircles:", isCircles);
    if (!isCircles) {
      // Reset box state when not in circles mode
      setShowBox(false);
      setBoxState("closed");
      return;
    }

    const animationCycle = async () => {
      console.log("Starting animation cycle");

      // Start by releasing shapes from the box (initial exit)
      console.log("Setting to releasing");
      setBoxState("releasing");

      // Wait for shapes to exit (reduced timing)
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // Wait in orbiting state (reduced)
      await new Promise((resolve) => setTimeout(resolve, 800));

      console.log("Setting box to show and opening");
      setShowBox(true);
      setBoxState("opening");

      // Wait for box to open (reduced)
      await new Promise((resolve) => setTimeout(resolve, 300));
      console.log("Setting to collecting");
      setBoxState("collecting");

      // Wait for shapes to enter (reduced)
      await new Promise((resolve) => setTimeout(resolve, 500));
      console.log("Setting to closed");
      setBoxState("closed");

      // Wait in closed state (reduced)
      await new Promise((resolve) => setTimeout(resolve, 800));
    };

    // Start the first cycle immediately
    animationCycle();

    // Set interval to match total cycle time: 1200 + 800 + 300 + 500 + 800 = 3600ms
    const interval = setInterval(animationCycle, 3600);
    return () => clearInterval(interval);
  }, [isCircles]); // Add isCircles as dependency

  if (isBulb) {
    return (
      <motion.svg
        width="400"
        height="300"
        viewBox="-25 -25 74 74"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          translateX: offsetCursorXBulb, // Center the cursor
          translateY: offsetCursorYBulb, // Center the cursor
          pointerEvents: "none",
          zIndex: 9999,
        }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{
          opacity: 1,
          scale: 1,
        }}
        transition={{
          opacity: { duration: 0.3 },
          scale: { duration: 1, type: "spring", stiffness: 300, damping: 30 },
        }}
      >
        {/* SVG Filter for glow effect */}
        <defs>
          <filter id="glassGlow" x="-250%" y="-250%" width="600%" height="600%">
            <feGaussianBlur stdDeviation="8" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="outerGlow" x="-400%" y="-400%" width="900%" height="900%">
            <feGaussianBlur stdDeviation="15" result="outerBlur" />
            <feMerge>
              <feMergeNode in="outerBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Bulb Holder - appears first with no glow */}
        <motion.path
          d="m11 18.7L16.5 18.7a3.85 3.85 90 00-.55 3.3 2.2 2.2 90 01-4.4 0 3.85 3.85 90 00-.55-3.3"
          fill={"#696446"}
          initial={{ pathLength: 0, opacity: 0, scale: 1.3, translateY: 2 }}
          animate={{ pathLength: 1, opacity: 1, scale: 1.3, translateY: 2 }}
          transition={{
            pathLength: { duration: 0.3, ease: "easeOut" },
            opacity: { duration: 0.3, ease: "easeOut" },
          }}
          style={{
            transformOrigin: "12.5px 16px",
            y: -2.8,
            x: -1.1, // Center the holder
          }}
        />

        {/* Outer animated glow layer - larger radius */}
        <motion.path
          d="m9 16a5 5 0 117 0M9 16h7"
          fill="#ff8c00"
          filter="url(#outerGlow)"
          initial={{ pathLength: 0, opacity: 0, scale: 2.8 }}
          animate={{
            pathLength: 1,
            opacity: [0.3, 0.5, 0.3, 0.7, 0.4],
            scale: [2.8, 3.5, 2.8, 3.2, 2.8],
            fill: ["#ff8c00", "#ffd700", "#ffb347", "#ff6347", "#ffa500"],
          }}
          transition={{
            pathLength: { duration: 0.4, ease: "easeOut", delay: 0.2 },
            opacity: {
              duration: 4,
              ease: "easeInOut",
              delay: 0.2,
              repeat: Infinity,
              repeatType: "loop",
            },
            scale: {
              duration: 5,
              ease: "easeInOut",
              delay: 0.2,
              repeat: Infinity,
              repeatType: "loop",
            },
            fill: {
              duration: 6,
              ease: "easeInOut",
              delay: 0.2,
              repeat: Infinity,
              repeatType: "loop",
            },
          }}
          style={{
            transformOrigin: "12.5px 16px",
            filter: "blur(3px)",
          }}
        />

        {/* Inner animated glow layer */}
        <motion.path
          d="m9 16a5 5 0 117 0M9 16h7"
          fill="#ffb347"
          filter="url(#glassGlow)"
          initial={{ pathLength: 0, opacity: 0, scale: 2.2 }}
          animate={{
            pathLength: 1,
            opacity: [0.6, 0.7, 0.5, 0.9, 0.6],
            scale: [2.2, 2.6, 2.2, 2.4, 2.2],
            fill: ["#ffb347", "#ffd700", "#ff8c00", "#ffa500", "#ffb347"],
          }}
          transition={{
            pathLength: { duration: 0.4, ease: "easeOut", delay: 0.2 },
            opacity: {
              duration: 3.5,
              ease: "easeInOut",
              delay: 0.3,
              repeat: Infinity,
              repeatType: "loop",
            },
            scale: {
              duration: 4,
              ease: "easeInOut",
              delay: 0.3,
              repeat: Infinity,
              repeatType: "loop",
            },
            fill: {
              duration: 4.5,
              ease: "easeInOut",
              delay: 0.3,
              repeat: Infinity,
              repeatType: "loop",
            },
          }}
          style={{
            transformOrigin: "12.5px 16px",
            filter: "blur(2px)",
          }}
        />

        {/* Main bulb glass - solid, no animation */}
        <motion.path
          d="m9 16a5 5 0 117 0M9 16h7"
          fill={currentColor}
          initial={{ pathLength: 0, opacity: 0, scale: 0.96 }}
          animate={{ pathLength: 1, opacity: 1, scale: 1.2 }}
          transition={{
            pathLength: { duration: 0.4, ease: "easeOut", delay: 0.2 },
            opacity: { duration: 0.4, ease: "easeOut", delay: 0.2 },
            scale: {
              duration: 0.5,
              ease: "easeOut",
              delay: 0.2,
              type: "spring",
              stiffness: 200,
              damping: 10,
            },
          }}
          style={{
            transformOrigin: "12.5px 16px",
          }}
        />

        {/* Inner bright core - subtle flicker */}
        <motion.path
          d="m9 16a5 5 0 117 0M9 16h7"
          fill="#ffffff"
          initial={{ pathLength: 0, opacity: 0, scale: 0.72 }}
          animate={{
            pathLength: 1,
            opacity: [0.7, 0.8, 0.6, 0.9, 0.7],
            scale: [0.72, 0.84, 0.72, 0.78, 0.72],
          }}
          transition={{
            pathLength: { duration: 0.3, ease: "easeOut", delay: 0.4 },
            opacity: {
              duration: 3,
              ease: "easeInOut",
              delay: 0.4,
              repeat: Infinity,
              repeatType: "loop",
            },
            scale: {
              duration: 2.5,
              ease: "easeInOut",
              delay: 0.4,
              repeat: Infinity,
              repeatType: "loop",
            },
          }}
          style={{
            transformOrigin: "12.5px 16px",
            filter: "blur(2px)",
          }}
        />
      </motion.svg>
    );
  }

  if (isCircles) {
    return (
      <motion.svg
        width="200"
        height="300"
        viewBox="0 0 30 30"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          translateX: offsetCursorXCircles,
          translateY: offsetCursorYCircles,
          pointerEvents: "none",
          zIndex: 9999,
        }}
        initial={{ opacity: 0, scale: 0, rotate: 0 }}
        animate={{
          opacity: 1,
          scale: 0.8,
          rotate: 0,
        }}
        transition={{
          opacity: { duration: 0.3 },
          scale: { duration: 0.5, type: "spring", stiffness: 300, damping: 30 },
        }}
      >
        {/* Main circle - starts inside box, exits smoothly */}
        <motion.circle
          cx="15"
          cy="15"
          r="2"
          fill={currentColor}
          style={{ filter: `drop-shadow(0px 0px 0.1rem ${currentColor})` }}
          initial={{ opacity: 1, scale: 1, x: 0, y: 6 }} // Start inside box
          animate={{
            opacity: boxState === "closed" ? 0 : 1,
            scale: 1,
            x: boxState === "collecting" || boxState === "closed" ? 0 : -3.6,
            y: boxState === "collecting" || boxState === "closed" ? 6 : -5.5, // Exit from box (y=6) to above (y=-5.5)
          }}
          transition={{
            opacity: { duration: 0.3 },
            scale: {
              duration: 0.5,
              type: "spring",
              stiffness: 200,
              damping: 20,
            },
            x: {
              duration: boxState === "releasing" ? 1 : 0.3,
              type: "spring",
              bounce: 0.2,
              delay: boxState === "releasing" ? 0.5 : 0, // Delay when releasing
            },
            y: {
              duration: 1,
              type: "spring",
              stiffness: 150,
              damping: 12,
              delay: boxState === "releasing" ? 0.5 : 0, // Delay when releasing
            },
          }}
        />

        {/* Triangle - starts inside box, exits smoothly */}
        <motion.path
          d="M13.4 6.871a1.33 1.33 90 012.3036 0l2.0014 3.4665a1.33 1.33 90 01-1.1518 1.995l-4.0027 0a1.33 1.33 90 01-1.1518-1.995L13.4 6.871"
          fill="#aa0cff"
          initial={{
            opacity: 1,
            scale: 0.65,
            x: 1.6,
            y: 12,
          }}
          animate={{
            opacity: boxState === "closed" ? 0 : 1,
            scale: 0.65,
            x: boxState === "collecting" || boxState === "closed" ? 1.6 : 2.7,
            y: boxState === "collecting" || boxState === "closed" ? 12 : 0, // Exit from box (y=12) to above (y=0)
          }}
          transition={{
            opacity: { duration: 0.3 },
            scale: {
              duration: 0.5,
              type: "spring",
              stiffness: 200,
              damping: 20,
            },
            x: {
              duration: boxState === "releasing" ? 1 : 0.3,
              delay:
                boxState === "collecting"
                  ? 0.2
                  : boxState === "releasing"
                    ? 0.7
                    : 0, // Delay when releasing
              bounce: 0.2,
              type: "spring",
            },
            y: {
              duration: 1,
              delay:
                boxState === "collecting"
                  ? 0.2
                  : boxState === "releasing"
                    ? 0.7
                    : 0, // Delay when releasing
              type: "spring",
              stiffness: 150,
              damping: 15,
            },
          }}
          style={{
            transformOrigin: "15px 9.83px",
            filter: `drop-shadow(0px 0px 0.1rem #aa0cff)`,
          }}
        />

        {/* Square - starts inside box, exits smoothly */}
        <motion.path
          d="M22.9496 18.5026a.8.8 135 01.8.8l0 1.9a.8.8 135 01-.8.8l-1.9 0a.8.8 135 01-.8-.8l0-1.9a.8.8 135 01.8-.8L22.9496 18.5026"
          fill="#6861ff"
          initial={{
            opacity: 1,
            scale: 1.2,
            x: -7.9,
            y: 2,
          }}
          animate={{
            opacity: boxState === "closed" ? 0 : 1,
            scale: 1.2,
            x: boxState === "collecting" || boxState === "closed" ? -7.9 : 0,
            y: boxState === "collecting" || boxState === "closed" ? 2 : -7, // Exit from box (y=2) to above (y=-7)
          }}
          transition={{
            opacity: { duration: 0.3 },
            scale: {
              duration: 0.5,
              type: "spring",
              stiffness: 180,
              damping: 18,
            },
            x: {
              duration: boxState === "releasing" ? 1 : 0.3,
              delay:
                boxState === "collecting"
                  ? 0.4
                  : boxState === "releasing"
                    ? 0.9
                    : 0, // Delay when releasing
              bounce: 0.2,
              type: "spring",
            },
            y: {
              duration: 1,
              delay:
                boxState === "collecting"
                  ? 0.4
                  : boxState === "releasing"
                    ? 0.9
                    : 0, // Delay when releasing
              type: "spring",
              stiffness: 140,
              damping: 12,
            },
          }}
          style={{
            transformOrigin: "22.25px 20.25px",
            filter: `drop-shadow(0px 0px 0.1rem #6861ff)`,
          }}
        />

        {/* Hexagon - starts inside box, exits smoothly */}
        <motion.path
          d="M6.775 17.325 4.9537 18.3758a.95.95 90 00-.475.8227V21.3015a.95.95 90 00.475.8227L6.775 23.1758a.95.95 90 00.95 0l1.8213-1.0515a.95.95 90 00.475-.8227V19.1985a.95.95 90 00-.475-.8227L7.725 17.3242a.95.95 90 00-.95 0"
          fill="#009dfe"
          initial={{
            opacity: 1,
            scale: 0.71,
            x: 8,
            y: 1,
          }}
          animate={{
            opacity: boxState === "closed" ? 0 : 1,
            scale: 0.71,
            x: boxState === "collecting" || boxState === "closed" ? 8 : 0,
            y: boxState === "collecting" || boxState === "closed" ? 1 : -7, // Exit from box (y=1) to above (y=-7)
          }}
          transition={{
            opacity: { duration: 0.3 },
            scale: {
              duration: 0.6,
              type: "spring",
              stiffness: 160,
              damping: 14,
            },
            x: {
              duration: boxState === "releasing" ? 1 : 0.3,
              delay:
                boxState === "collecting"
                  ? 0.6
                  : boxState === "releasing"
                    ? 1.1
                    : 0, // Delay when releasing
              bounce: 0.2,
              type: "spring",
            },
            y: {
              duration: 1,
              delay:
                boxState === "collecting"
                  ? 0.6
                  : boxState === "releasing"
                    ? 1.1
                    : 0, // Delay when releasing
              type: "spring",
              stiffness: 130,
              damping: 10,
            },
          }}
          style={{
            transformOrigin: "10px 20.25px",
            filter: `drop-shadow(0px 0px 0.1rem #009dfe)`,
          }}
        />

        {/* Box Container - appears after shapes, renders on top */}
        {showBox && (
          <motion.g>
            {/* Box base - side view - rendered on top of flaps */}
            <motion.rect
              x="10"
              y="18"
              width="10"
              height="9"
              rx="0.5"
              fill="#c69f5b"
              strokeWidth="0.5"
              initial={{ opacity: 0, y: 2, scale: 0.8 }}
              style={{
                filter: "drop-shadow(0px 0px 4px black)",
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 1,
                y: 2,
                scale: 0.8,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                duration: 0.4,
              }}
            />

            {/* Box flap - animated to open/close */}
            <motion.rect
              x="20"
              y="18"
              width="2"
              height="10"
              rx="0.5"
              fill="#c69f5b"
              stroke="#a47a45"
              strokeWidth="0.3"
              transform={"translate(0, 12)"}
              style={{
                y: -10,
                x: -12,
              }}
              initial={{
                rotate: 90,
                transformOrigin: "right bottom",
                translateX: 0,
              }}
              animate={{
                transformOrigin: "right bottom",
                translateX:
                  boxState === "opening" || boxState === "releasing" ? 10 : 0,
              }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                duration: 0.3,
                translateX: {
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                  duration: 0.3,
                  delay: boxState === "releasing" ? 0 : 0.6,
                },
              }}
            />
          </motion.g>
        )}
      </motion.svg>
    );
  }

  if (isHeartShape) {
    return (
      <motion.div
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          translateX: offsetCursorX,
          translateY: offsetCursorY,
          pointerEvents: "none",
          zIndex: 9999,
          width: "200px",
          height: "120px",
          scale: 0.75,
        }}
      >
        {/* Backdrop blur layer with heart mask */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            x: 40,
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            backgroundColor: "rgba(200, 200, 200, 0)", // Add slight background for testing
            clipPath:
              "path('M61.2 108.885l-7.395-6.732C27.54 78.336 10.2 62.628 10.2 43.35 10.2 27.642 22.542 15.3 38.25 15.3c8.874 0 17.391 4.131 22.95 10.659C66.759 19.431 75.276 15.3 84.15 15.3 99.858 15.3 112.2 27.642 112.2 43.35c0 19.278-17.34 34.986-43.605 58.854L61.2 108.885z')",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, scale: [1, 1.2, 1, 1.1, 1] }}
          transition={{
            scale: {
              duration: 0.4,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 0.5,
            },
          }}
        />

        {/* Heart shape on top */}
        <motion.svg
          width="200"
          height="120"
          viewBox="0 0 24 24"
          style={{
            position: "absolute",
            filter: `drop-shadow(0px 0px 0.1rem ${currentColor}) drop-shadow(0px 0px 0.8rem ${currentColor})  drop-shadow(0px 0px 0.5rem ${currentColor})`,
          }}
          animate={{
            scale: [1, 1.2, 1, 1.1, 1],
            opacity: 1,
          }}
          transition={{
            scale: {
              duration: 0.4,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 0.5,
            },
          }}
        >
          <motion.path
            d={currentPath}
            fill="rgba(255, 255, 255, 0.27)"
            stroke="rgba(255, 255, 255, 0.3)"
            strokeWidth={0.2}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </motion.svg>
      </motion.div>
    );
  }
  return (
    <motion.svg
      width="200"
      height="120"
      viewBox="0 0 24 24"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        translateX: offsetCursorX, // Center the cursor
        translateY: offsetCursorY, // Center the cursor
        pointerEvents: "none",
        zIndex: 9999,
        mixBlendMode: currentColor === "#00ffff" ? "difference" : "normal",
        filter: `drop-shadow(0px 0px 0.8rem ${currentColor})`,
        scale: 0.75,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ opacity: { duration: 0.3 } }}
    >
      {currentPath ? (
        <motion.path
          d={currentPath}
          fill={currentColor}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      ) : (
        <circle cx="12" cy="12" r="8" fill={currentColor} />
      )}
    </motion.svg>
  );
};

const CustomCursor: React.FC = () => {
  // Use Motion Values for smooth animation, especially for position
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  // Spring animation for the cursor position for a natural feel
  const springConfig = {
    stiffness: 1000, // Higher stiffness - less 'wiggle'
    damping: 25, // Lower damping - more 'bounce' (adjust as needed)
    mass: 0.3, // Lower mass - quicker reaction
  };

  const smoothCursorX = useSpring(cursorX, springConfig);
  const smoothCursorY = useSpring(cursorY, springConfig);

  const [isClicked, setIsClicked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOverInput, setIsOverInput] = useState(false);
  const {
    isHovering,
    isPointer,
    setIsPointer,
    hoverText,
    isMorphed,
    morphStyle,
    shapePaths,
    shapeColors,
  } = useCursor();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Update motion values directly
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      if (!isVisible) setIsVisible(true);

      const target = e.target as HTMLElement;

      // Check if hovering over any input field
      const isInputField =
        target.closest(`
        input,
        textarea,
        select,
        [contenteditable="true"],
        [contenteditable=""],
        .input,
        [role="textbox"],
        [role="searchbox"],
        [role="combobox"]
      `) !== null;
      setIsOverInput(isInputField);

      const targetIsPointer =
        window.getComputedStyle(target).getPropertyValue("cursor") ===
          "pointer" ||
        target.closest(
          'a, button, input[type="button"], input[type="submit"], [role="button"], [data-cursor-none]',
        ) !== null;
      setIsPointer(targetIsPointer);
    };

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => {
      setIsVisible(false);
      setIsPointer(false); // Reset on leaving window
      setIsOverInput(false); // Reset input state
      //   cursorX.set(-100); // Move off-screen smoothly
      //   cursorY.set(-100);
    };
    const handleMouseDown = () => setIsClicked(true);
    const handleMouseUp = () => setIsClicked(false);

    // Add listeners
    window.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseenter", handleMouseEnter);
    document.body.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    // Cleanup
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseenter", handleMouseEnter);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // Rerun effect if setIsPointer changes (it shouldn't often, but good practice)
  }, [cursorX, cursorY, isVisible, setIsPointer]);

  // Define variants for different cursor states using Tailwind
  const cursorVariants: Variants = {
    initial: {
      opacity: 0,
      scale: 0,
      x: "-50%",
      y: "-50%",
    },
    default: {
      opacity: 1,
      backgroundColor: "#DB0703",
      scale: 1,
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      x: "-50%",
      y: "-50%",
      clipPath: "circle(50% at 50% 50%)",
      transition: { type: "spring", stiffness: 400, damping: 25, mass: 0.4 },
    },
    morphed: {
      opacity: 1,
      borderRadius: "0px",
      ...morphStyle,
      x: "-50%",
      y: "-50%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        mass: 0.5,
      },
    },
    hovering: {
      backgroundColor: "#DB0703",
      opacity: 1,
      scale: 2,
      width: "35px",
      height: "35px",
      borderRadius: "99999px",
      x: "-50%",
      y: "-50%",
      clipPath: "circle(50% at 50% 50%)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        mass: 0.5,
        duration: 0.3,
      },
    },
    pointer: {
      scale: 1,
      opacity: 1,
      backgroundColor: "#DB0703",
      width: "21px",
      height: "28px",
      borderRadius: "4px", // Remove border radius for arrow shape
      rotate: 0,
      x: "-20%",
      y: "-20%",
      clipPath: "polygon(0 0, 100% 58%, 35% 59%, 0% 100%)", // Arrow shape
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 20,
      },
    },
    clicked: {
      scale: 0.5,
      opacity: 1,
      // backgroundColor: "#DB0703",
      // width: "36px",
      // height: "36px",
      // borderRadius: "50%",
      // rotate: 180,
      // transition: {
      //   type: "spring",
      //   stiffness: 500,
      //   damping: 15,
      //   duration: 0.2,
      // },
    },
    input: {
      scale: 1,
      opacity: 1,
      backgroundColor: "#DB0703",
      width: "2px",
      height: "28px",
      borderRadius: "1px",
      rotate: 0,
      x: "-50%",
      y: "-50%",
      clipPath: "polygon(0 0, 100% 0%, 100% 100%, 0% 100%)", // Vertical line
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 20,
      },
    },
  };

  // Determine the current variant string
  let currentVariant = "default";
  if (!isVisible) currentVariant = "initial";
  // Order matters: Click overrides all, then morphed, then input, then hover/pointer
  else if (isClicked) currentVariant = "clicked";
  else if (isMorphed) currentVariant = "morphed";
  else if (isOverInput) currentVariant = "input";
  else if (isHovering) currentVariant = "hovering";
  else if (isPointer) currentVariant = "pointer";

  // Basic check to hide on touch devices
  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  if (isTouchDevice) {
    return null;
  }

  if (isMorphed && shapePaths.length > 0) {
    return (
      <MorphingCursor
        shapePaths={shapePaths}
        shapeColors={shapeColors}
        smoothCursorX={smoothCursorX}
        smoothCursorY={smoothCursorY}
      />
    );
  }

  return (
    <motion.div
      // Base styles using Tailwind - applied always
      className={clsx(
        "fixed left-0 top-0 z-[9999] rounded-full pointer-events-none",
      )}
      // Motion values for position (use the smoothed ones)
      style={{
        translateX: smoothCursorX,
        translateY: smoothCursorY,
        mixBlendMode: isMorphed ? "difference" : "normal",
      }}
      // Animate between variants
      variants={cursorVariants}
      initial="initial" // Start hidden/off-screen
      animate={currentVariant}
    >
      {isHovering && hoverText && !isOverInput && (
        <div
          className="absolute whitespace-nowrap text-white text-[8px] font-medium rounded-full text-center flex flex-col items-center justify-center"
          style={{
            color: isMorphed ? "#000" : "#fff",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        >
          <GoChevronUp
            size={18}
            style={{
              display: isMorphed ? "block" : "none",
              marginTop: isMorphed ? "-8px" : "0",
            }}
          />
          <div
            style={{
              marginTop: isMorphed ? "-4px" : "0",
            }}
          >
            {hoverText}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CustomCursor;
